import React, { useState, useRef, useEffect } from 'react';  
import { motion } from 'framer-motion';  
import { Pause, Play } from 'lucide-react';  
import { cartasLotería } from './data/cartas';  
import { barajarCartas } from './utils/shuffle';  
import ControlTiempo from './components/ControlTiempo';  
import CartaActual from './components/CartaActual';  
import BotonIniciar from './components/BotonIniciar';  
import ToggleVoz from './components/ToggleVoz';  
import Historial from './components/Historial';  
import VerificarCartilla from './components/VerificarCartilla';  
import Watermark from './components/Watermark';  // Importar la marca de agua  
  
const App = () => {  
  const [mazoBarajado, setMazoBarajado] = useState([]);  
  const [cartaActual, setCartaActual] = useState(null);  
  const [indiceCarta, setIndiceCarta] = useState(0);  
  const [tiempoEntreCartas, setTiempoEntreCartas] = useState(3);  
  const [estadoJuego, setEstadoJuego] = useState('idle'); // idle, playing, paused  
  const [vozActivada, setVozActivada] = useState(true);  
  const [historial, setHistorial] = useState([]);  
  const [mostrarVerificar, setMostrarVerificar] = useState(false);  
  const totalCartas = cartasLotería.length;  
  const intervaloRef = useRef(null);  
  const mazoBarajadoRef = useRef([]);  
  const indiceCartaRef = useRef(0);  
  
  // Cargar historial desde localStorage al iniciar  
  useEffect(() => {  
    const savedHistorial = localStorage.getItem('loteriaHistorial');  
    if (savedHistorial) {  
      setHistorial(JSON.parse(savedHistorial));  
    }  
  }, []);  
  
  // Guardar historial en localStorage cuando cambie  
  useEffect(() => {  
    if (historial.length > 0) {  
      localStorage.setItem('loteriaHistorial', JSON.stringify(historial));  
    }  
  }, [historial]);  
  
  const hablarCarta = (speakText) => {  
    if (!vozActivada) return;  
    speechSynthesis.cancel();  
    const utterance = new SpeechSynthesisUtterance(speakText);  
    utterance.lang = 'es-ES';  
    utterance.rate = 0.8; // Habla un poco más lento para claridad  
    speechSynthesis.speak(utterance);  
  };  
  
  const iniciarJuego = () => {  
    const mazoNuevo = barajarCartas(cartasLotería);  
    mazoBarajadoRef.current = mazoNuevo;  
    indiceCartaRef.current = 0;  
    setMazoBarajado(mazoNuevo);  
    setIndiceCarta(0);  
    setCartaActual(mazoNuevo[0]);  
    hablarCarta(mazoNuevo[0].speak_text);  
    setHistorial([mazoNuevo[0]]);  
    setEstadoJuego('playing');  
    // Si estaba pausado, no barajar, solo reanudar  
  };  
  
  const pausarJuego = () => {  
    if (intervaloRef.current) {  
      clearInterval(intervaloRef.current);  
      intervaloRef.current = null;  
    }  
    if (vozActivada) {  
      speechSynthesis.cancel();  
    }  
    setEstadoJuego('paused');  
  };  
  
  const reanudarJuego = () => {  
    if (estadoJuego === 'paused' && mazoBarajadoRef.current.length > 0) {  
      setEstadoJuego('playing');  
      intervaloRef.current = setInterval(() => {  
        const currentIndex = indiceCartaRef.current;  
        if (currentIndex + 1 < mazoBarajadoRef.current.length) {  
          indiceCartaRef.current = currentIndex + 1;  
          setIndiceCarta(indiceCartaRef.current);  
          const siguiente = mazoBarajadoRef.current[indiceCartaRef.current];  
          setCartaActual(siguiente);  
          hablarCarta(siguiente.speak_text);  
          setHistorial(prev => [...prev, siguiente]);  
        } else {  
          detenerJuego();  
        }  
      }, tiempoEntreCartas * 1000);  
    }  
  };  
  
  const detenerJuego = () => {  
    if (intervaloRef.current) {  
      clearInterval(intervaloRef.current);  
      intervaloRef.current = null;  
    }  
    if (vozActivada) {  
      speechSynthesis.cancel();  
    }  
    setEstadoJuego('idle');  
    mazoBarajadoRef.current = [];  
    indiceCartaRef.current = 0;  
    setCartaActual(null);  
    setIndiceCarta(0);  
    setMazoBarajado([]);  
  };  
  
  const handleControlJuego = () => {  
    if (estadoJuego === 'idle') {  
      iniciarJuego();  
      intervaloRef.current = setInterval(() => {  
        const currentIndex = indiceCartaRef.current;  
        if (currentIndex + 1 < mazoBarajadoRef.current.length) {  
          indiceCartaRef.current = currentIndex + 1;  
          setIndiceCarta(indiceCartaRef.current);  
          const siguiente = mazoBarajadoRef.current[indiceCartaRef.current];  
          setCartaActual(siguiente);  
          hablarCarta(siguiente.speak_text);  
          setHistorial(prev => [...prev, siguiente]);  
        } else {  
          detenerJuego();  
        }  
      }, tiempoEntreCartas * 1000);  
    } else if (estadoJuego === 'playing') {  
      pausarJuego();  
    } else if (estadoJuego === 'paused') {  
      reanudarJuego();  
    }  
  };  
  
  const limpiarHistorial = () => {  
    setHistorial([]);  
    localStorage.removeItem('loteriaHistorial');  
    detenerJuego();  
  };  
  
  useEffect(() => {  
    return () => {  
      if (intervaloRef.current) {  
        clearInterval(intervaloRef.current);  
      }  
      if (vozActivada) {  
        speechSynthesis.cancel();  
      }  
    };  
  }, []);  
  
  return (  
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-100 to-yellow-100 p-4 relative">  {/* Relative para watermark */}  
      <motion.h1  
        className="text-4xl font-bold text-gray-800 mb-6 text-center"  
        initial={{ y: -50, opacity: 0 }}  
        animate={{ y: 0, opacity: 1 }}  
        transition={{ duration: 0.8 }}  
      >  
        ¡La Lotería Mexicana MC! 🎉  
      </motion.h1>  
      <p className="text-center text-gray-600 mb-6 text-sm">Instala esta app en tu dispositivo para jugar offline. Busca el botón de instalación en tu navegador.</p>  
  
      <div className="max-w-md mx-auto space-y-4">  
        <ControlTiempo  
          tiempoEntreCartas={tiempoEntreCartas}  
          onTiempoChange={setTiempoEntreCartas}  
        />  
  
        <div className="flex gap-4 justify-center">  
          <ToggleVoz vozActivada={vozActivada} onToggleVoz={() => setVozActivada(!vozActivada)} />  
        </div>  
  
        <BotonIniciar onControl={handleControlJuego} estadoJuego={estadoJuego} />  
  
        {cartaActual && (  
          <div className="space-y-4">  
            <CartaActual  
              carta={cartaActual}  
              onHablar={() => hablarCarta(cartaActual.speak_text)}  
            />  
  
            {/* Botones dentro del juego: Verificar solo si hay historial */}  
            {historial.length > 0 && estadoJuego !== 'idle' && (  
              <div className="flex gap-2 justify-center">  
                <button  
                  onClick={() => setMostrarVerificar(true)}  
                  className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-xl font-medium flex items-center gap-2 text-sm"  
                >  
                  Verificar Cartilla  
                </button>  
                {estadoJuego === 'playing' && (  
                  <button  
                    onClick={pausarJuego}  
                    className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-xl font-medium flex items-center gap-2 text-sm"  
                  >  
                    <Pause className="w-4 h-4" /> Pausar  
                  </button>  
                )}  
                {estadoJuego === 'paused' && (  
                  <button  
                    onClick={reanudarJuego}  
                    className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-xl font-medium flex items-center gap-2 text-sm"  
                  >  
                    <Play className="w-4 h-4" /> Reanudar  
                  </button>  
                )}  
              </div>  
            )}  
  
            {mazoBarajado.length > 0 && (  
              <p className="text-center text-gray-600">Carta {indiceCarta + 1} de {totalCartas}</p>  
            )}  
          </div>  
        )}  
  
        <Historial historial={historial} onLimpiarHistorial={limpiarHistorial} />  
      </div>  
  
      <Watermark />  {/* Agregar la marca de agua */}  
  
      {mostrarVerificar && (  
        <VerificarCartilla  
          historial={historial}  
          onCerrar={() => setMostrarVerificar(false)}  
        />  
      )}  
    </div>  
  );  
};  
  
export default App;